import {
  ArrowLeftRight,
  ArrowRight,
  CircleCheck,
  HandCoins,
  Plus,
  Scale,
  Trash2,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useActiveGroup } from '@/features/groups/store';
import {
  useBalances,
  useLedgerStore,
  useMemberLookup,
  useSettlementPlan,
} from '@/features/ledger/store';
import { formatDate, todayISO } from '@/lib/date';
import {
  Avatar,
  Button,
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  IconButton,
  Input,
  Modal,
  Money,
  PageHeader,
  Select,
  StatTile,
  balancePhrase,
  confirm,
  toast,
  type Column,
} from '@/ui';
import { amountInputValue, currencySymbol, formatMoney, parseAmountInput, sum } from '@domain/money';
import type { MemberBalance, Settlement, SettlementSuggestion } from '@domain/types';

type Draft = {
  fromMemberId: string;
  toMemberId: string;
  amount: string;
  date: string;
  note: string;
};

const EMPTY_DRAFT: Draft = {
  fromMemberId: '',
  toMemberId: '',
  amount: '',
  date: todayISO(),
  note: '',
};

export function SettlePage() {
  const navigate = useNavigate();

  const group = useActiveGroup();
  const members = useLedgerStore((state) => state.members);
  const settlements = useLedgerStore((state) => state.settlements);
  const addSettlement = useLedgerStore((state) => state.addSettlement);
  const removeSettlement = useLedgerStore((state) => state.removeSettlement);

  const balances = useBalances();
  const plan = useSettlementPlan();
  const memberLookup = useMemberLookup();

  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const currency = group?.currency ?? 'USD';
  const outstandingMinor = sum(plan.map((suggestion) => suggestion.amountMinor));
  const settledMinor = sum(settlements.map((settlement) => settlement.amountMinor));

  function openManual(prefill?: SettlementSuggestion) {
    setFormError(null);
    setDraft(
      prefill
        ? {
            fromMemberId: prefill.fromMemberId,
            toMemberId: prefill.toMemberId,
            amount: amountInputValue(prefill.amountMinor),
            date: todayISO(),
            note: '',
          }
        : { ...EMPTY_DRAFT, fromMemberId: members[0]?.id ?? '', toMemberId: members[1]?.id ?? '' }
    );
    setModalOpen(true);
  }

  async function save() {
    const amountMinor = parseAmountInput(draft.amount);

    if (!draft.fromMemberId || !draft.toMemberId) {
      setFormError('Choose who paid and who received the money.');
      return;
    }
    if (draft.fromMemberId === draft.toMemberId) {
      setFormError('A settlement needs two different members.');
      return;
    }
    if (!amountMinor || amountMinor <= 0) {
      setFormError('Enter an amount greater than zero.');
      return;
    }

    setSaving(true);
    try {
      await addSettlement({
        fromMemberId: draft.fromMemberId,
        toMemberId: draft.toMemberId,
        amountMinor,
        date: draft.date,
        note: draft.note,
      });
      toast.success('Payment recorded.');
      setModalOpen(false);
    } catch (error) {
      setFormError((error as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(settlement: Settlement) {
    const from = memberLookup.get(settlement.fromMemberId)?.name ?? 'Unknown';
    const to = memberLookup.get(settlement.toMemberId)?.name ?? 'Unknown';

    const ok = await confirm({
      title: 'Delete this settlement?',
      message: `${from} → ${to} will be removed and their balances will reopen.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;

    await removeSettlement(settlement.id);
    toast.success('Settlement deleted.');
  }

  if (!group) {
    return (
      <>
        <PageHeader title="Settle up" />
        <EmptyState
          icon={Users}
          title="No group selected"
          description="Choose a group to settle up."
          action={<Button onClick={() => navigate('/groups')}>Choose a group</Button>}
        />
      </>
    );
  }

  const balanceColumns: Column<MemberBalance>[] = [
    {
      key: 'member',
      header: 'Member',
      cell: (row) => (
        <span className="flex items-center gap-2.5">
          <Avatar name={row.memberName} id={row.memberId} size="sm" dimmed={!row.isActive} />
          <span className="truncate font-medium">{row.memberName}</span>
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      hideOnNarrow: true,
      cell: (row) => <span className="text-muted">{balancePhrase(row.netMinor, currency)}</span>,
    },
    {
      key: 'settled',
      header: 'Settled',
      align: 'right',
      hideOnNarrow: true,
      cell: (row) => <Money minor={row.settledMinor} currency={currency} muted />,
    },
    {
      key: 'net',
      header: 'Net',
      align: 'right',
      width: '130px',
      cell: (row) => (
        <Money minor={row.netMinor} currency={currency} signed className="font-semibold" />
      ),
    },
  ];

  const historyColumns: Column<Settlement>[] = [
    {
      key: 'transfer',
      header: 'Transfer',
      cell: (row) => {
        const from = memberLookup.get(row.fromMemberId)?.name ?? 'Unknown';
        const to = memberLookup.get(row.toMemberId)?.name ?? 'Unknown';
        return (
          <span className="flex items-center gap-2">
            <Avatar name={from} id={row.fromMemberId} size="xs" />
            <span className="truncate">{from}</span>
            <ArrowRight size={13} className="shrink-0 text-muted" />
            <Avatar name={to} id={row.toMemberId} size="xs" />
            <span className="truncate">{to}</span>
          </span>
        );
      },
    },
    {
      key: 'date',
      header: 'Date',
      align: 'right',
      hideOnNarrow: true,
      cell: (row) => <span className="text-muted">{formatDate(row.date)}</span>,
    },
    {
      key: 'note',
      header: 'Note',
      hideOnNarrow: true,
      cell: (row) => <span className="text-muted">{row.note ?? '—'}</span>,
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      width: '130px',
      cell: (row) => (
        <Money minor={row.amountMinor} currency={currency} className="font-semibold" />
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '56px',
      cell: (row) => (
        <IconButton
          icon={Trash2}
          label="Delete settlement"
          size="sm"
          onClick={() => handleDelete(row)}
        />
      ),
    },
  ];

  const memberOptions = members.map((member) => ({
    value: member.id,
    label: member.isActive ? member.name : `${member.name} (inactive)`,
  }));

  return (
    <>
      <PageHeader
        title="Settle up"
        eyebrow={group.name}
        actions={
          <Button icon={Plus} onClick={() => openManual()} disabled={members.length < 2}>
            Record payment
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        <StatTile
          className="col-span-2 sm:col-span-1"
          label="Outstanding"
          icon={Scale}
          value={formatMoney(outstandingMinor, currency)}
          caption={`${plan.length} payment${plan.length === 1 ? '' : 's'} to clear it`}
          tone={outstandingMinor === 0 ? 'success' : 'default'}
        />
        <StatTile
          label="Already settled"
          icon={HandCoins}
          tone="success"
          value={formatMoney(settledMinor, currency)}
          caption={`${settlements.length} recorded`}
        />
        <StatTile label="Members" icon={Users} value={String(members.length)} />
      </div>

      <section className="mt-6">
        <CardHeader
          title="Suggested payments"
          description="The fewest transfers that clear every debt in this group."
        />

        {plan.length === 0 ? (
          <Card>
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <span className="mb-1 inline-flex size-14 items-center justify-center rounded-2xl bg-success/12 text-success ring-8 ring-success/5">
                <CircleCheck size={26} />
              </span>
              <p className="text-base font-semibold">All settled up</p>
              <p className="max-w-sm text-[13px] text-muted">
                {members.length === 0
                  ? 'Add members and expenses to see who owes what.'
                  : 'Nobody in this group owes anybody anything.'}
              </p>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {plan.map((suggestion) => (
              <Card
                key={`${suggestion.fromMemberId}-${suggestion.toMemberId}`}
                className="flex min-w-0 items-center gap-3 p-4"
              >
                <span className="flex shrink-0 items-center">
                  <Avatar name={suggestion.fromMemberName} id={suggestion.fromMemberId} />
                  <span className="z-10 -mx-1.5 inline-flex size-6 items-center justify-center rounded-full bg-surface text-muted ring-1 ring-line">
                    <ArrowRight size={12} strokeWidth={2.5} />
                  </span>
                  <Avatar name={suggestion.toMemberName} id={suggestion.toMemberId} />
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12px] text-muted">
                    {suggestion.fromMemberName} pays {suggestion.toMemberName}
                  </span>
                  <Money
                    minor={suggestion.amountMinor}
                    currency={currency}
                    className="text-base font-bold"
                  />
                </span>

                <Button
                  size="sm"
                  variant="secondary"
                  icon={ArrowLeftRight}
                  onClick={() => openManual(suggestion)}
                >
                  Record
                </Button>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section className="mt-8">
        <CardHeader title="Balances" />
        <DataTable
          columns={balanceColumns}
          rows={[...balances].sort((a, b) => b.netMinor - a.netMinor)}
          rowKey={(row) => row.memberId}
          onRowClick={(row) => navigate(`/members/${row.memberId}`)}
          empty={<EmptyState title="No members yet" description="Add members to track balances." />}
        />
      </section>

      <section className="mt-8">
        <CardHeader title={`Settlement history (${settlements.length})`} />
        <DataTable
          columns={historyColumns}
          rows={settlements}
          rowKey={(row) => row.id}
          empty={
            <EmptyState
              title="No payments recorded"
              description="Once someone pays someone back, record it here and the balances update."
            />
          }
        />
      </section>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Record a payment"
        description="Money that has actually changed hands."
        width="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button loading={saving} onClick={save}>
              Record payment
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Select
            label="Who paid"
            value={draft.fromMemberId}
            placeholder="Select a member"
            options={memberOptions}
            onChange={(event) =>
              setDraft((current) => ({ ...current, fromMemberId: event.target.value }))
            }
          />
          <Select
            label="Who received"
            value={draft.toMemberId}
            placeholder="Select a member"
            options={memberOptions}
            onChange={(event) =>
              setDraft((current) => ({ ...current, toMemberId: event.target.value }))
            }
          />
          <Input
            label="Amount"
            placeholder="0.00"
            prefix={currencySymbol(currency)}
            inputMode="decimal"
            value={draft.amount}
            onChange={(event) =>
              setDraft((current) => ({ ...current, amount: event.target.value }))
            }
          />
          <Input
            label="Date"
            type="date"
            value={draft.date}
            onChange={(event) => setDraft((current) => ({ ...current, date: event.target.value }))}
          />
          <Input
            label="Note (optional)"
            placeholder="Bank transfer, cash…"
            value={draft.note}
            onChange={(event) => setDraft((current) => ({ ...current, note: event.target.value }))}
          />

          {formError && (
            <p role="alert" className="rounded-xl bg-danger/10 px-3 py-2 text-[12px] text-danger">
              {formError}
            </p>
          )}
        </div>
      </Modal>
    </>
  );
}
